import {
    Box,
    Checkbox,
    useToast,
    Link as ChakraLink,
    Text,
    Spinner,
    Flex,
    VStack,
    HStack,
    Divider,
    SimpleGrid,
    Button,
} from '@chakra-ui/react';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import { useForm } from 'react-hook-form';
import {
    companyNameSchema,
    emailSchema,
    nameSchema,
    passSchema,
    passwordSchema,
    phoneSchema,
} from '../../utils/schemas';
import { PATHS } from '../../router/routes';
import { useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Register } from '../../graphql/mutations/Register';
import { useError } from '../../error-provider/ErrorProvider';
import FormInput from '../../components/custom-components/Inputs/FormInput';
import { INPUT_PROPS } from '../../utils/constants';
import { Turnstile } from '@marsidev/react-turnstile';
import SocialButtons from '../../components/custom-components/buttons/SocialButtons';
import { useAuth } from '../../provider';
import useServerApi from 'hooks/useServerApi';
import ZaraLogo from '../../components/header/ZaraLogo';

const TERMS_OF_USE = 'https://dialzara.com/terms-of-use';
const PRIVACY_POLICY = 'https://dialzara.com/privacy-policy';

// Senja widget IDs
const SENJA_DESKTOP_WIDGET_ID = 'ef50750b-5b1b-44d7-beb9-da938a3fea90';
const SENJA_MOBILE_WIDGET_ID = 'aba8ef84-c747-4488-86c3-db9de97f09b3';
const inputs = [
    {
        label: INPUT_PROPS.FIRST_NAME.LABEL,
        controllName: INPUT_PROPS.FIRST_NAME.CONTROLL_NAME,
        placeholder: INPUT_PROPS.FIRST_NAME.PLACEHOLDER,
    },
    {
        label: INPUT_PROPS.LAST_NAME.LABEL,
        controllName: INPUT_PROPS.LAST_NAME.CONTROLL_NAME,
        placeholder: INPUT_PROPS.LAST_NAME.PLACEHOLDER,
    },
    {
        label: INPUT_PROPS.COMPANY.LABEL,
        controllName: INPUT_PROPS.COMPANY.CONTROLL_NAME,
        placeholder: INPUT_PROPS.COMPANY.PLACEHOLDER,
    },
    {
        label: INPUT_PROPS.EMAIL.LABEL,
        controllName: INPUT_PROPS.EMAIL.CONTROLL_NAME,
        placeholder: INPUT_PROPS.EMAIL.PLACEHOLDER,
    },
    {
        label: INPUT_PROPS.PHONE_NUMBER.LABEL,
        controllName: INPUT_PROPS.PHONE_NUMBER.CONTROLL_NAME,
        placeholder: INPUT_PROPS.PHONE_NUMBER.PLACEHOLDER,
    },
    {
        label: 'Must be at least 8 characters with 1 capital letter, 1 number, and 1 special character',
        controllName: INPUT_PROPS.PASSWORD.CONTROLL_NAME,
        placeholder: INPUT_PROPS.PASSWORD.PLACEHOLDER,
    },
    {
        controllName: INPUT_PROPS.CONFIRM_PASSWORD.CONTROLL_NAME,
        placeholder: INPUT_PROPS.CONFIRM_PASSWORD.PLACEHOLDER,
    },
];

const schema = z
    .object({
        email: emailSchema,
        password: passwordSchema,
        confirmPassword: passSchema,
        firstName: nameSchema,
        lastName: nameSchema,
        companyName: companyNameSchema,
        phoneNumber: phoneSchema,
    })
    .refine((data) => data.password === data.confirmPassword && data.confirmPassword === data.password, {
        message: "Passwords don't match",
        path: ['password'],
    })
    .refine((data) => /(?=.*[A-Z])/.test(data.password), {
        message: 'Password does not have uppercase letter.',
        path: ['password'],
    })
    .refine((data) => /(?=.*[a-z])/.test(data.password), {
        message: 'Password does not have lowercase letter.',
        path: ['password'],
    })
    .refine((data) => /(?=.*\d)/.test(data.password), {
        message: 'Password does not have digit.',
        path: ['password'],
    })
    .refine((data) => /(?=.*[!@#$%^&*])/.test(data.password), {
        message: 'Password does not have special character.',
        path: ['password'],
    });

const SignUp = () => {
    const toast = useToast();
    const router = useNavigate();
    const { control, handleSubmit, watch } = useForm({
        defaultValues: {
            email: '',
            password: '',
            confirmPassword: '',
            firstName: '',
            lastName: '',
            companyName: '',
            phoneNumber: '',
        },
        mode: 'onSubmit',
        resolver: zodResolver(schema),
    });
    const { showError } = useError();
    const [onRegister] = useMutation(Register, {
        onError(error) {
            showError(undefined, error);
        },
    });
    const [loading, setLoading] = useState(false);
    const { signIn, loading: authLoading } = useAuth();
    const [hideNewPass, setHideNewPass] = useState(true);
    const [hideConfirmPass, setHideConfirmPass] = useState(true);
    const [isAccept, setIsAccept] = useState(false);
    const [captcha, setCaptcha] = useState('');

    const captchaRef = useRef();
    const [{ get }] = useServerApi();
    const [turnstileSiteKey, setTurnstileSiteKey] = useState('');
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

    useEffect(() => {
        const matchingSub = ['app.dialzara.com', 'dev-app.dialzara.com'].find(sub => window.location.host.includes(sub));
        if (matchingSub) {
            const currentPath = window.location.pathname + window.location.search;
            if (window.location.host === 'dev-app.dialzara.com') {
                window.location.href = 'https://dev-preview.dialzara.com' + currentPath;
                return;
            }
            else if (window.location.host === 'app.dialzara.com') {
                window.location.href = 'https://web.dialzara.com' + currentPath;
                return;
            }
        }
    }, []);

    // Track viewport size for Senja widget selection (lg breakpoint = 1024px)
    useEffect(() => {
        let resizeTimer;
        const handleResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                setIsDesktop(window.innerWidth >= 1024);
            }, 250); // Debounce resize events by 250ms
        };

        window.addEventListener('resize', handleResize);
        return () => {
            clearTimeout(resizeTimer);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Load appropriate Senja widget based on viewport
    useEffect(() => {
        const widgetId = isDesktop ? SENJA_DESKTOP_WIDGET_ID : SENJA_MOBILE_WIDGET_ID;

        // Helper function to remove Senja scripts
        const removeSenjaScripts = () => {
            const scripts = document.querySelectorAll('script[src*="senja.io"]');
            scripts.forEach(script => script.remove());
        };

        // Remove any existing Senja scripts before loading new one
        removeSenjaScripts();

        // Load the Senja widget script
        const script = document.createElement('script');
        script.src = `https://widget.senja.io/widget/${widgetId}/platform.js`;
        script.async = true;
        script.type = 'text/javascript';
        document.body.appendChild(script);

        return () => {
            // Clean up script on unmount or when switching widgets
            removeSenjaScripts();
        };
    }, [isDesktop]);

    useEffect(() => {

        async function fetchSiteKey() {
            setLoading(true);
            const [success, data] = await get('/rest_endpoints/accounts/cloudfare_sitekey');
            if (success && data && data.site_key) {
                setTurnstileSiteKey(data.site_key);
                setLoading(false);
            }
        }
        fetchSiteKey();
    }, [get]);

    const email = watch('email');
    const password = watch('password');
    const confirmPassword = watch('confirmPassword');
    const firstName = watch('firstName');
    const lastName = watch('lastName');
    const companyName = watch('companyName');
    const phoneNumber = watch('phoneNumber');

    const isDisabled = useMemo(() => {
        if (
            !email.length ||
            !password.length ||
            !confirmPassword.length ||
            !firstName.length ||
            !lastName.length ||
            !companyName.length ||
            !phoneNumber.length ||
            !isAccept ||
            !captcha.length
        ) {
            return true;
        }
        return false;
    }, [email, password, confirmPassword, firstName, lastName, companyName, isAccept, phoneNumber, captcha]);

    const handleCaptchaChange = (value) => {
        setCaptcha(value);
    };

    async function onSubmit(data) {
        setLoading(true); // global loading
        const variables = {
            companyName: data.companyName,
            email: data.email.toLowerCase(),
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: '+' + data.phoneNumber,
            password1: `${data.password}`,
            password2: `${data.confirmPassword}`,
            recaptchaCode: captcha,
            timeZone: data.timeZone,
        };

        try {
            await onRegister({
                variables: variables,
                onCompleted: async (data) => {
                    if (data.register.__typename === 'ErrorType') {
                        toast({
                            status: 'error',
                            title: data.register.message,
                            isClosable: true,
                        });
                        if (captchaRef.current) {
                            captchaRef.current.reset();
                        }
                        setLoading(false);
                    }

                    if (data.register.__typename === 'SuccessType') {
                        // Persist sign-up data locally (original behavior retained)
                        localStorage.setItem('email', variables.email);
                        localStorage.setItem('phoneNumber', variables.phoneNumber);
                        localStorage.removeItem('fromWhere');
                        // Automatically sign the user in using the same credentials
                        try {
                            await signIn({ email: variables.email, password: variables.password1 });
                        } catch (e) {
                            // fallback to verification page if signIn fails for any reason
                            router(PATHS.VERIFICATION);
                        }
                    }
                },
            });
        } catch (error) {
            setLoading(false);
        }
    }
    if (turnstileSiteKey === '') {
        return (
            <Flex minH="100vh" bgGradient="linear(to-br, #3a0ca3, #5611f8, #7209b7)" align="center" justify="center">
                <VStack spacing={4}>
                    <Text color="white" fontSize="lg">Loading...</Text>
                    <Spinner size="xl" color="white" thickness="4px" speed="0.65s" />
                </VStack>
            </Flex>
        );
    }
    return (
        <Flex
            minH="100vh"
            flexDirection="column"
            bgGradient="linear(to-br, #3a0ca3, #5611f8, #7209b7)"
        >
            {/* Navbar */}
            <Flex
                as="nav"
                h="56px"
                px={{ base: 4, md: 10 }}
                align="center"
                justify={{ base: 'center', md: 'space-between' }}
            >
                {/* Logo - white via filter */}
                <Box
                    display={{ base: 'flex', md: 'flex' }}
                    sx={{
                        filter: 'brightness(0) invert(1)',
                    }}
                >
                    <ZaraLogo />
                </Box>

                {/* Sign In link - Desktop only */}
                <ChakraLink
                    display={{ base: 'none', md: 'block' }}
                    color="white"
                    fontSize="14px"
                    onClick={() => router(PATHS.SIGN_IN)}
                    cursor="pointer"
                    _hover={{ opacity: 0.8 }}
                >
                    Sign In
                </ChakraLink>
            </Flex>

            {/* Main */}
            <Flex
                flex="1"
                align="center"
                justify="center"
                px={{ base: 4, md: 8 }}
                py={{ base: 6, md: 8 }}
            >
                <Flex
                    direction={{ base: 'column', lg: 'row' }}
                    gap={{ base: 8, lg: '32px', xl: '40px' }}
                    align="flex-start"
                    maxW="1200px"
                    w="100%"
                >
                    {/* Form Card */}
                    <Box
                        bg="white"
                        borderRadius="16px"
                        boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.2)"
                        p={{ base: '24px', md: '40px' }}
                        w="100%"
                        maxW="520px"
                        mx={{ base: 'auto', lg: 0 }}
                    >
                        <VStack spacing="16px" align="stretch">
                            {/* Heading */}
                            <Box>
                                <Text fontSize="24px" fontWeight="bold" mb="8px">
                                    Create your account
                                </Text>
                                <Text fontSize="14px" color="gray.600">
                                    Join 10,000+ businesses already using AI to automate phone calls.
                                </Text>
                            </Box>

                            {/* Form */}
                            <Box as="form" onSubmit={handleSubmit(onSubmit)}>
                                <VStack spacing="16px" align="stretch">
                                    {/* Row1: First Name and Last Name */}
                                    <SimpleGrid columns={[1, null, 2]} spacing={3}>
                                        <FormInput
                                            mb="0px"
                                            isSignUp={true}
                                            control={control}
                                            label={inputs[0].label}
                                            controllName={inputs[0].controllName}
                                            placeholder={inputs[0].placeholder}
                                            inputStyles={{
                                                height: '40px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                            }}
                                        />
                                        <FormInput
                                            mb="0px"
                                            isSignUp={true}
                                            control={control}
                                            label={inputs[1].label}
                                            controllName={inputs[1].controllName}
                                            placeholder={inputs[1].placeholder}
                                            inputStyles={{
                                                height: '40px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                            }}
                                        />
                                    </SimpleGrid>

                                    {/* Row2: Company */}
                                    <FormInput
                                        mb="0px"
                                        isSignUp={true}
                                        control={control}
                                        label={inputs[2].label}
                                        controllName={inputs[2].controllName}
                                        placeholder={inputs[2].placeholder}
                                        inputStyles={{
                                            height: '40px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                        }}
                                    />

                                    {/* Row3: Email */}
                                    <FormInput
                                        mb="0px"
                                        isSignUp={true}
                                        control={control}
                                        label={inputs[3].label}
                                        controllName={inputs[3].controllName}
                                        placeholder={inputs[3].placeholder}
                                        inputStyles={{
                                            height: '40px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                        }}
                                    />

                                    {/* Row4: Phone */}
                                    <FormInput
                                        mb="0px"
                                        isSignUp={true}
                                        control={control}
                                        label={inputs[4].label}
                                        controllName={inputs[4].controllName}
                                        placeholder={inputs[4].placeholder}
                                        inputStyles={{
                                            height: '40px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                        }}
                                    />

                                    {/* Row5: Password */}
                                    <FormInput
                                        mb="0px"
                                        isSignUp={true}
                                        control={control}
                                        label="Password"
                                        controllName={inputs[5].controllName}
                                        placeholder={inputs[5].placeholder}
                                        variant="password"
                                        hidePass={hideNewPass}
                                        setHidePass={setHideNewPass}
                                        inputStyles={{
                                            height: '40px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                        }}
                                    />

                                    {/* Password helper text */}
                                    <Text fontSize="11px" color="gray.600" mt="-8px">
                                        Must be at least 8 characters with 1 capital letter, 1 number, and 1 special character
                                    </Text>

                                    {/* Row5: Confirm Password */}
                                    <FormInput
                                        mb="0px"
                                        isSignUp={true}
                                        control={control}
                                        label={inputs[6].label || ''}
                                        controllName={inputs[6].controllName}
                                        placeholder={inputs[6].placeholder}
                                        variant="password"
                                        hidePass={hideConfirmPass}
                                        setHidePass={setHideConfirmPass}
                                        inputStyles={{
                                            height: '40px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                        }}
                                    />

                                    {/* Row6: Checkbox */}
                                    <Checkbox
                                        size="md"
                                        isChecked={isAccept}
                                        onChange={() => setIsAccept((prev) => !prev)}
                                    >
                                        <Text fontSize="11px" color="gray.600">
                                            I agree to receive automated followup, reminder, and promotional messages,
                                            calls, or email with varying frequency. Click for{' '}
                                            <ChakraLink
                                                href={TERMS_OF_USE}
                                                isExternal
                                                color="#5611f8"
                                                textDecoration="underline"
                                            >
                                                Terms of Service
                                            </ChakraLink>{' '}
                                            and{' '}
                                            <ChakraLink
                                                href={PRIVACY_POLICY}
                                                isExternal
                                                color="#5611f8"
                                                textDecoration="underline"
                                            >
                                                Privacy Policy
                                            </ChakraLink>
                                            . Msg and data rates may apply. Reply STOP to end.
                                        </Text>
                                    </Checkbox>

                                    {/* Row7: Turnstile */}
                                    <Box minH="80px">
                                        {turnstileSiteKey && (
                                            <Box
                                                maxW="100%"
                                                overflow="hidden"
                                            >
                                                <Box
                                                    transform={{ base: 'scale(0.85)', sm: 'scale(1)' }}
                                                    transformOrigin="left center"
                                                >
                                                    <Turnstile
                                                        ref={captchaRef}
                                                        siteKey={turnstileSiteKey}
                                                        onSuccess={handleCaptchaChange}
                                                    />
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Row8: Submit Button */}
                                    <Button
                                        type="submit"
                                        isLoading={loading || authLoading}
                                        isDisabled={isDisabled || loading || authLoading}
                                        w="100%"
                                        h="44px"
                                        borderRadius="8px"
                                        bg="#5611f8"
                                        color="white"
                                        fontSize="14px"
                                        fontWeight="600"
                                        rightIcon={<ArrowForwardIcon />}
                                        _hover={{ opacity: 0.9 }}
                                    >
                                        Sign Up with Email
                                    </Button>
                                </VStack>
                            </Box>

                            {/* Below form: Already have an account */}
                            <Text fontSize="14px" color="gray.600" textAlign="center">
                                Already have an account?{' '}
                                <Text
                                    as="span"
                                    color="#5611f8"
                                    fontWeight="bold"
                                    cursor="pointer"
                                    onClick={() => router(PATHS.SIGN_IN)}
                                    _hover={{ textDecoration: 'underline' }}
                                >
                                    Sign In
                                </Text>
                            </Text>

                            {/* Divider with OR CONTINUE WITH */}
                            <HStack spacing={3}>
                                <Divider />
                                <Text
                                    fontSize="12px"
                                    textTransform="uppercase"
                                    letterSpacing="wide"
                                    color="gray.500"
                                    whiteSpace="nowrap"
                                >
                                    OR CONTINUE WITH
                                </Text>
                                <Divider />
                            </HStack>

                            {/* Social buttons */}
                            <SocialButtons type="iconRow" />
                        </VStack>
                    </Box>

                    {/* Desktop Senja Widget - Hide on mobile */}
                    <Box
                        display={{ base: 'none', lg: 'block' }}
                        maxW={{ lg: '480px', xl: '600px' }}
                        w="100%"
                    >
                        <div
                            className="senja-embed"
                            data-id={SENJA_DESKTOP_WIDGET_ID}
                            data-lazyload="false"
                        ></div>
                    </Box>
                </Flex>
            </Flex>

            {/* Mobile Senja Widget - Show on mobile only */}
            <Box
                display={{ base: 'block', lg: 'none' }}
                px={4}
                pb={6}
                mt={8}
            >
                <div
                    className="senja-embed"
                    data-id={SENJA_MOBILE_WIDGET_ID}
                    data-lazyload="false"
                ></div>
            </Box>

            {/* Footer */}
            <Box py={6} px={{ base: 4, md: 10 }}>
                {/* Desktop Footer */}
                <Flex
                    display={{ base: 'none', md: 'flex' }}
                    justify="space-between"
                    align="center"
                >
                    <Text
                        fontSize="12px"
                        color="whiteAlpha.400"
                        textTransform="uppercase"
                        letterSpacing="wide"
                    >
                        © 2026 DIALZARA INC. ALL RIGHTS RESERVED.
                    </Text>
                    <HStack spacing="24px">
                        <ChakraLink
                            href="https://dialzara.com/privacy-policy"
                            isExternal
                            fontSize="12px"
                            color="whiteAlpha.400"
                            textTransform="uppercase"
                            letterSpacing="wide"
                            _hover={{ color: 'white' }}
                        >
                            PRIVACY
                        </ChakraLink>
                        <Text fontSize="12px" color="whiteAlpha.400">
                            |
                        </Text>
                        <ChakraLink
                            href="https://dialzara.com/terms-of-use"
                            isExternal
                            fontSize="12px"
                            color="whiteAlpha.400"
                            textTransform="uppercase"
                            letterSpacing="wide"
                            _hover={{ color: 'white' }}
                        >
                            TERMS
                        </ChakraLink>
                        <Text fontSize="12px" color="whiteAlpha.400">
                            |
                        </Text>
                        <Text
                            as="button"
                            onClick={() => {
                                if (window.$crisp) {
                                    window.$crisp.push(['do', 'chat:open']);
                                }
                            }}
                            fontSize="12px"
                            color="whiteAlpha.400"
                            textTransform="uppercase"
                            letterSpacing="wide"
                            _hover={{ color: 'white' }}
                        >
                            SUPPORT
                        </Text>
                    </HStack>
                </Flex>

                {/* Mobile Footer */}
                <VStack
                    display={{ base: 'flex', md: 'none' }}
                    spacing={4}
                    align="center"
                >
                    <HStack spacing="24px">
                        <ChakraLink
                            href="https://dialzara.com/privacy-policy"
                            isExternal
                            fontSize="12px"
                            color="whiteAlpha.400"
                            textTransform="uppercase"
                            letterSpacing="wide"
                            _hover={{ color: 'white' }}
                        >
                            PRIVACY
                        </ChakraLink>
                        <Text fontSize="12px" color="whiteAlpha.400">
                            |
                        </Text>
                        <ChakraLink
                            href="https://dialzara.com/terms-of-use"
                            isExternal
                            fontSize="12px"
                            color="whiteAlpha.400"
                            textTransform="uppercase"
                            letterSpacing="wide"
                            _hover={{ color: 'white' }}
                        >
                            TERMS
                        </ChakraLink>
                        <Text fontSize="12px" color="whiteAlpha.400">
                            |
                        </Text>
                        <Text
                            as="button"
                            onClick={() => {
                                if (window.$crisp) {
                                    window.$crisp.push(['do', 'chat:open']);
                                }
                            }}
                            fontSize="12px"
                            color="whiteAlpha.400"
                            textTransform="uppercase"
                            letterSpacing="wide"
                            _hover={{ color: 'white' }}
                        >
                            SUPPORT
                        </Text>
                    </HStack>
                    <Text
                        fontSize="12px"
                        color="whiteAlpha.400"
                        textTransform="uppercase"
                        letterSpacing="wide"
                        textAlign="center"
                    >
                        © 2026 DIALZARA INC.
                    </Text>
                </VStack>
            </Box>
        </Flex>
    );
};

export default SignUp;
